import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { accountsApi } from '../../api/accounts'
import { RefusedError } from '../../api/client'
import { LineIcon } from '../../components/LineIcon'
import { rail, roadmapDate, STATE_WORDS, windowLabel, windowTone } from '../../lib/roadmap'
import type { RoadmapLine, RoadmapPatch, RoadmapState, RoadmapView } from '../../types/accounts'

// The account roadmap (REQ-051): the rounds of engagement planned after the work under way, in order, each with
// the window in which opening it is optimal. Scout proposes the sequence from the land-and-expand openings; the
// person reorders, retimes, states or drops a round, and once they have touched it a re-propose leaves it alone.
// Presentation only — the sequence, the windows and their state all arrive computed from the API.

const STATES: RoadmapState[] = ['proposed', 'planned', 'open', 'done', 'dropped']

export function Roadmap({ spoke, companyId, companyName }: { spoke: string; companyId: string; companyName: string }) {
  const client = useQueryClient()
  const q = useQuery({ queryKey: ['roadmap', spoke, companyId], queryFn: () => accountsApi.roadmap(spoke, companyId), staleTime: 15_000 })
  const [error, setError] = useState<string | null>(null)

  const onDone = (view: RoadmapView) => {
    client.setQueryData(['roadmap', spoke, companyId], view)
    client.invalidateQueries({ queryKey: ['account', spoke, companyId] })
    setError(null)
  }
  const onFail = (e: unknown) => setError(e instanceof RefusedError ? e.refusal.message : e instanceof Error ? e.message : 'That did not work.')

  const propose = useMutation({ mutationFn: () => accountsApi.proposeRoadmap(spoke, companyId), onSuccess: onDone, onError: onFail })
  const patch = useMutation({
    mutationFn: ({ itemId, body }: { itemId: string; body: RoadmapPatch }) => accountsApi.patchRoadmap(spoke, companyId, itemId, body),
    onSuccess: onDone, onError: onFail,
  })
  const drop = useMutation({ mutationFn: (itemId: string) => accountsApi.dropRoadmap(spoke, companyId, itemId), onSuccess: onDone, onError: onFail })
  const busy = propose.isPending || patch.isPending || drop.isPending

  const lines = q.data?.items ?? []
  const asOf = q.data?.asOf ?? ''
  const track = useMemo(() => (asOf ? rail(lines, asOf) : null), [lines, asOf])
  const openNow = lines.filter((l) => l.window.state === 'open' || l.window.state === 'closing')
  const proposed = lines.filter((l) => l.item.state === 'proposed').length

  return (
    <section className="card roadmap-card" aria-label="Account roadmap">
      <div className="card-head">
        <div>
          <h2>Roadmap</h2>
          <p className="muted">
            The rounds planned after the work under way, in order, each with the window in which opening it is optimal.
            Scout proposes the sequence from the ways this account could expand; you own it from the moment you touch a round.
          </p>
        </div>
        <button className="button small" onClick={() => propose.mutate()} disabled={busy}
          title="Lay out (or refresh) Scout's proposal from the land-and-expand openings. A round you have already ordered, retimed or stated is never written over.">
          {propose.isPending ? 'Proposing…' : lines.length === 0 ? 'Propose a roadmap' : 'Refresh the proposal'}
        </button>
      </div>

      {error && <p className="error">{error}</p>}
      {q.isPending && <p className="muted">Loading…</p>}
      {q.isError && <div className="refusal"><strong>Unavailable</strong><p>{q.error.message}</p></div>}

      {q.isSuccess && lines.length === 0 && (
        <div className="ways-empty">
          <strong>No roadmap at this account yet.</strong>
          <p className="muted">
            Propose one and Scout lays out the next rounds from the lines this account is not engaged on, with a window on each.
            Reorder them, move the dates, and the window that opens reaches your brief.
          </p>
        </div>
      )}

      {openNow.length > 0 && (
        <div className={`roadmap-callout ${windowTone(openNow[0].window)}`}>
          <LineIcon name="next-step" size={16} />
          <p>
            <strong>{openNow.length === 1 ? 'One round is in its window' : `${openNow.length} rounds are in their window`}</strong>
            {' '}at {companyName}: {openNow.map((l) => l.item.serviceLine).join(', ')}. This is the moment to open the conversation.
          </p>
        </div>
      )}

      {track && (
        <svg className="roadmap-rail" viewBox="0 0 1000 46" preserveAspectRatio="none" role="img"
          aria-label={`Timeline of ${track.bars.length} planned rounds from today`}>
          <line className="rail-axis" x1={0} y1={34} x2={1000} y2={34} />
          {track.months.map((m) => (
            <g key={m.label}>
              <line className="rail-tick" x1={m.at * 1000} y1={30} x2={m.at * 1000} y2={38} />
              <text className="rail-label" x={m.at * 1000} y={46} textAnchor="middle">{m.label}</text>
            </g>
          ))}
          <line className="rail-now" x1={track.todayAt * 1000} y1={0} x2={track.todayAt * 1000} y2={38} />
          {track.bars.map((b, i) => (
            <rect key={b.id} className={`rail-bar ${b.tone} ${b.state}`} x={b.from * 1000} y={2 + (i % 3) * 9}
              width={Math.max(6, (b.to - b.from) * 1000)} height={7} rx={3}>
              <title>{`${b.label} — ${STATE_WORDS[b.state]}`}</title>
            </rect>
          ))}
        </svg>
      )}

      {lines.length > 0 && (
        <ol className="roadmap-list">
          {lines.map((l, i) => (
            <Round key={l.item.roadmapItemId} line={l} first={i === 0} last={i === lines.length - 1} busy={busy}
              onPatch={(body) => patch.mutate({ itemId: l.item.roadmapItemId, body })}
              onDrop={() => drop.mutate(l.item.roadmapItemId)} />
          ))}
        </ol>
      )}

      {lines.length > 0 && (
        <p className="roadmap-foot muted small">
          As of {roadmapDate(asOf)}. {proposed === 0 ? 'Every round here is yours.' : `${proposed} still Scout's suggestion — order or retime one and it becomes yours.`}
          {' '}A planned round whose window has opened reaches your brief.
        </p>
      )}
    </section>
  )
}

function Round({ line, first, last, busy, onPatch, onDrop }: {
  line: RoadmapLine; first: boolean; last: boolean; busy: boolean
  onPatch: (body: RoadmapPatch) => void; onDrop: () => void
}) {
  const { item, window: w } = line
  const tone = windowTone(w)
  return (
    <li className={`roadmap-round ${item.state} ${tone}`}>
      <span className="roadmap-rank" aria-hidden="true">{item.order}</span>
      <div className="roadmap-body">
        <div className="roadmap-head">
          <strong className="roadmap-line-name">{item.serviceLine}</strong>
          <span className={`tag roadmap-state ${item.state}`}>{STATE_WORDS[item.state]}</span>
          <span className={`roadmap-window ${tone}`}>{windowLabel(line)}</span>
        </div>
        {item.why && <p className="roadmap-why">{item.why}</p>}
        {item.fromOpeningLine
          ? <p className="roadmap-source muted small">Proposed from the {item.fromOpeningLine} opening.</p>
          : <p className="roadmap-source muted small">Added by hand.</p>}
        <div className="roadmap-controls">
          <span className="roadmap-control-group">
            <button className="button tiny" disabled={busy || first} onClick={() => onPatch({ order: item.order - 1 })} title="Move this round earlier in the sequence">Earlier</button>
            <button className="button tiny" disabled={busy || last} onClick={() => onPatch({ order: item.order + 1 })} title="Move this round later in the sequence">Later</button>
          </span>
          <label className="roadmap-field">
            <span>Window opens</span>
            <input type="date" value={item.windowOpens ?? ''} disabled={busy} onChange={(e) => onPatch({ windowOpens: e.target.value })} />
          </label>
          <label className="roadmap-field">
            <span>Closes</span>
            <input type="date" value={item.windowCloses ?? ''} disabled={busy} onChange={(e) => onPatch({ windowCloses: e.target.value })} />
          </label>
          <label className="roadmap-field">
            <span>State</span>
            <select value={item.state} disabled={busy} onChange={(e) => onPatch({ state: e.target.value as RoadmapState })}>
              {STATES.map((s) => <option key={s} value={s}>{STATE_WORDS[s]}</option>)}
            </select>
          </label>
          <button className="button tiny danger" disabled={busy} onClick={onDrop} title="Take this round off the roadmap entirely. To keep it on file instead, set its state to Dropped.">Remove</button>
        </div>
      </div>
    </li>
  )
}
